import type { Booking } from '@/types';
import { bookingOverlaps } from '@/lib/morocco';

export interface CalendarBar {
  id: string;
  lane: number;
  startCol: number;
  endCol: number;
  continuesBefore: boolean;
  continuesAfter: boolean;
}

function pad(value: number) {
  return value.toString().padStart(2, '0');
}

/** Monday-first month grid. Leading and trailing cells are empty. */
export function monthCells(year: number, month: number): (string | null)[] {
  const leading = (new Date(year, month, 1).getDay() + 6) % 7;
  const count = new Date(year, month + 1, 0).getDate();
  const cells: (string | null)[] = Array.from({ length: leading }, () => null);
  for (let day = 1; day <= count; day += 1) {
    cells.push(`${year}-${pad(month + 1)}-${pad(day)}`);
  }
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

export function chunkWeeks(cells: (string | null)[]): (string | null)[][] {
  const weeks: (string | null)[][] = [];
  for (let index = 0; index < cells.length; index += 7) {
    weeks.push(cells.slice(index, index + 7));
  }
  return weeks;
}

/**
 * Pack overlapping reservations into lanes for one Monday-first week.
 * A bar that ends the day before the next one starts can share a lane.
 */
export function weekBars(days: (string | null)[], bookings: Booking[]): CalendarBar[] {
  const realDays = days.filter((day): day is string => day !== null);
  const weekStart = realDays[0];
  const weekEnd = realDays[realDays.length - 1];
  if (!weekStart || !weekEnd) return [];

  const overlapping = bookings
    .filter((booking) => bookingOverlaps(weekStart, weekEnd, booking.pickupDate, booking.dropoffDate))
    .slice()
    .sort((left, right) => left.pickupDate.localeCompare(right.pickupDate)
      || left.dropoffDate.localeCompare(right.dropoffDate)
      || left.id.localeCompare(right.id));

  const laneEnds: number[] = [];
  const bars: CalendarBar[] = [];

  for (const booking of overlapping) {
    const continuesBefore = booking.pickupDate < weekStart;
    const continuesAfter = booking.dropoffDate > weekEnd;
    const startCol = days.indexOf(continuesBefore ? weekStart : booking.pickupDate);
    const endCol = days.indexOf(continuesAfter ? weekEnd : booking.dropoffDate);
    if (startCol < 0 || endCol < 0 || endCol < startCol) continue;

    let lane = laneEnds.findIndex((endColumn) => endColumn < startCol);
    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(endCol);
    } else {
      laneEnds[lane] = endCol;
    }

    bars.push({
      id: booking.id,
      lane,
      startCol,
      endCol,
      continuesBefore,
      continuesAfter,
    });
  }

  return bars;
}

export function overflowOnColumn(column: number, bars: CalendarBar[], visibleLanes: number) {
  return bars.filter((bar) => bar.lane >= visibleLanes && bar.startCol <= column && column <= bar.endCol).length;
}
